import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, User, Heart, PlusCircle, X, Home, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import { useIsMobile } from '@/hooks/use-mobile';
import Logo from '@/components/ui/Logo';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getUnreadMessageCount } from '@/lib/actions';
import NotificationDropdown from './NotificationDropdown';

const Navbar = () => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { profile, isAuthenticated } = useAuth();
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isAdmin = profile?.role === 'admin';
  
  // Check if the current path is one of the bottom navigation routes
  const isActive = (path: string) => location.pathname === path;
  
  // Handler for creating a new listing - UPDATED to always navigate to create-listing
  const handleCreateListing = () => {
    // Always navigate to create listing page, regardless of authentication status
    navigate('/create-listing');
  };
  
  // Handle search submission
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
      setIsSearchOpen(false);
    }
  };

  // Handler for navigating to favorites page
  const handleFavoritesClick = () => {
    if (isAuthenticated) {
      navigate('/favorites');
    } else {
      navigate('/auth');
    }
  };
  
  // Handler for navigating to messages page
  const handleMessagesClick = () => {
    if (isAuthenticated) {
      navigate('/messages');
    } else {
      navigate('/auth');
    }
  };
  
  // Fetch unread message count using React Query
  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['unreadCount', profile?.id, location.pathname],
    queryFn: async () => {
      if (!isAuthenticated || !profile) return 0;
      
      try {
        return await getUnreadMessageCount(profile.id);
      } catch (error) {
        console.error('Error fetching unread messages:', error);
        return 0;
      }
    },
    enabled: !!isAuthenticated && !!profile,
    refetchInterval: 5000, // Refetch every 5 seconds to keep the count updated
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });
  
  // Set up real-time subscription for new messages and message updates
  useEffect(() => {
    if (!isAuthenticated || !profile) return;
    
    const messageInsertSubscription = supabase
      .channel('message-inserts')
      .on('postgres_changes', { 
        event: 'INSERT', 
        schema: 'public', 
        table: 'messages'
      }, (payload) => {
        if (payload.new && payload.new.sender_id !== profile.id) {
          // Force refetch when new message is received
          queryClient.invalidateQueries({ queryKey: ['unreadCount'] });
        }
      })
      .subscribe();
      
    const messageUpdateSubscription = supabase
      .channel('message-updates')
      .on('postgres_changes', { 
        event: 'UPDATE', 
        schema: 'public', 
        table: 'messages'
      }, () => {
        // Force refetch when message is updated
        queryClient.invalidateQueries({ queryKey: ['unreadCount'] });
      })
      .subscribe();
    
    return () => {
      supabase.removeChannel(messageInsertSubscription);
      supabase.removeChannel(messageUpdateSubscription);
    };
  }, [isAuthenticated, profile, queryClient]);
  
  // Special effect: if coming from messages page, force refresh the unread count
  useEffect(() => {
    if (location.pathname === '/messages' && profile?.id) {
      queryClient.invalidateQueries({ queryKey: ['unreadCount'] });
    }
  }, [location.pathname, profile?.id, queryClient]);
  
  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg border-b border-gray-100 dark:border-gray-800 transition-all duration-300">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <Logo size="medium" />

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-6">
              {isAdmin && (
                <Link to="/admin-dashboard" className="text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-green-500 dark:hover:text-green-400 transition-colors">
                  Admin
                </Link>
              )}
            </nav>

            {/* Desktop/Mobile Search and Favorites */}
            <div className="flex items-center space-x-2">
              {/* Notification Bell - New Component */}
              {isAuthenticated && <NotificationDropdown />}
              
              {/* Favorites Icon */}
              <Button 
                variant="ghost" 
                size="icon" 
                className="rounded-full dark:text-gray-300 dark:hover:bg-gray-800"
                onClick={handleFavoritesClick}
              >
                <Heart size={20} />
              </Button>

              {isSearchOpen ? (
                <div className="relative animate-fade-in w-full max-w-xs">
                  <form onSubmit={handleSearch}>
                    <Input
                      type="search"
                      placeholder="Kërko produktet..."
                      className="w-full pr-8 rounded-full dark:bg-gray-800 dark:text-gray-100"
                      autoFocus
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                      onClick={() => setIsSearchOpen(false)}
                    >
                      <X size={18} />
                    </button>
                  </form>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <button
                    className="p-2 rounded-full text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    onClick={() => setIsSearchOpen(true)}
                  >
                    <Search size={20} />
                  </button>
                </div>
              )}
            </div>

            {/* Desktop Right Actions */}
            <div className="hidden md:flex items-center space-x-4">
              <div className="relative">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="rounded-full dark:text-gray-300 dark:hover:bg-gray-800"
                  onClick={handleMessagesClick}
                >
                  <MessageCircle size={20} />
                  {unreadCount > 0 && (
                    <Badge 
                      variant="destructive" 
                      className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs"
                    >
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </Badge>
                  )}
                </Button>
              </div>
              
              <Link to="/profile">
                <Button variant="ghost" size="icon" className="rounded-full dark:text-gray-300 dark:hover:bg-gray-800">
                  <User size={20} />
                </Button>
              </Link>
              
              <Button 
                className="rounded-full bg-green-500 hover:bg-green-600 dark:bg-green-600 dark:hover:bg-green-700"
                onClick={handleCreateListing}
              >
                <PlusCircle size={16} className="mr-2" />
                Posto Shpallje
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <nav className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 z-50">
          <div className="flex items-center justify-around h-16">
            <Link to="/" className={`flex flex-col items-center justify-center flex-1 py-2 ${isActive('/') ? 'text-green-500 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}>
              <Home size={24} />
              <span className="text-xs mt-1">Kryefaqja</span>
            </Link>
            
            <button 
              onClick={() => {
                setIsSearchOpen(true);
                if (location.pathname !== '/search') {
                  navigate('/search');
                }
              }}
              className={`flex flex-col items-center justify-center flex-1 py-2 ${isActive('/search') ? 'text-green-500 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}
            >
              <Search size={24} />
              <span className="text-xs mt-1">Kërko</span>
            </button>
            
            <button 
              onClick={handleCreateListing}
              className={`flex flex-col items-center justify-center flex-1 py-2 ${isActive('/create-listing') ? 'text-green-500 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}
            >
              <PlusCircle size={28} className={`${isActive('/create-listing') ? 'text-green-500 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`} />
              <span className="text-xs mt-1">Shto</span>
            </button>
            
            <div className="relative flex flex-col items-center justify-center flex-1 py-2">
              <button 
                onClick={handleMessagesClick}
                className={`relative flex flex-col items-center ${isActive('/messages') ? 'text-green-500 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}
              >
                <MessageCircle size={24} />
                {unreadCount > 0 && (
                  <Badge 
                    variant="destructive" 
                    className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs"
                  >
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </Badge>
                )}
                <span className="text-xs mt-1">Mesazhet</span>
              </button>
            </div>
            
            <Link to="/profile" className={`flex flex-col items-center justify-center flex-1 py-2 ${isActive('/profile') ? 'text-green-500 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}>
              <User size={24} />
              <span className="text-xs mt-1">Profili</span>
            </Link>
          </div>
        </nav>
      )}
    </>
  );
};

export default Navbar;
